<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

interface TrustSignal {
  icon: 'lock' | 'shield' | 'verified'
  label: string
}

const signals: TrustSignal[] = [
  { icon: 'lock', label: 'Escrow' },
  { icon: 'shield', label: 'VARA' },
  { icon: 'verified', label: 'DLD' },
]

const SLICE_COUNT = 12
const OWNED_SLICES = 3
const slices = Array.from({ length: SLICE_COUNT }, (_, i) => i < OWNED_SLICES)
</script>

<template>
  <Box class="trust-dossier" direction="col" align="center" justify="center" padX="6" padY="6" gap="6">
    <Box direction="col" gap="2" align="start" class="trust-dossier__intro">
      <Text type="caption-sm" weight="semibold" color="primary" transform="uppercase">
        Built for UAE investors
      </Text>
      <Text is="h2" type="display" weight="bold" class="trust-dossier__headline">
        Own a slice of <Text is="span" color="primary">Dubai real estate.</Text>
      </Text>
    </Box>

    <Box class="trust-dossier__hero" direction="col" gap="2" align="center">
      <Box class="trust-dossier__slices">
        <Box
          v-for="(owned, i) in slices"
          :key="i"
          class="trust-dossier__slice"
          :class="{ 'trust-dossier__slice--owned': owned }"
        />
      </Box>
      <Text type="caption" color="muted" align="center">
        Each property is split into tokens · You own as few as one
      </Text>
    </Box>

    <Box direction="col" gap="1" align="center" class="trust-dossier__amount-block">
      <Text type="caption-sm" color="muted" transform="uppercase" weight="medium">
        Start investing from
      </Text>
      <Text class="trust-dossier__amount" weight="bold">AED 2,000</Text>
    </Box>

    <Box direction="col" gap="2" align="center" class="trust-dossier__signals-block">
      <Text type="caption-sm" weight="semibold" color="primary" transform="uppercase">
        Regulated &amp; escrow-backed
      </Text>
      <Box class="trust-dossier__signals" justify="center" align="center" gap="2">
        <Box
          v-for="signal in signals"
          :key="signal.label"
          class="trust-dossier__signal"
          align="center"
          gap="2"
        >
          <PiIcon :name="signal.icon" class="trust-dossier__signal-icon" />
          <Text type="body-sm" weight="semibold">{{ signal.label }}</Text>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
.trust-dossier {
  width: 100%;
  height: 100%;
  background:
    radial-gradient(circle at 20% 0%, rgba(255, 255, 255, 0.5), transparent 50%),
    radial-gradient(circle at 100% 100%, rgba(103, 85, 251, 0.10), transparent 50%),
    linear-gradient(160deg, var(--p-sliceal-sign-up-right) 0%, #efe9ff 100%);
  border-radius: 20px;
  box-sizing: border-box;
}

.trust-dossier__intro {
  width: 100%;
  max-width: 420px;
}

.trust-dossier__headline {
  font-size: clamp(1.625rem, 2.2vw, 2rem);
  line-height: 1.15;
  letter-spacing: -0.015em;
}

.trust-dossier__hero {
  width: 100%;
}

.trust-dossier__slices {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.5rem;
  width: 100%;
  max-width: 280px;
}

.trust-dossier__slice {
  aspect-ratio: 1;
  border-radius: 8px;
  background: rgba(103, 85, 251, 0.08);
  border: 1px solid rgba(103, 85, 251, 0.12);
}

.trust-dossier__slice--owned {
  background: var(--p-primary-600);
  border-color: var(--p-primary-700);
  box-shadow: 0 4px 10px -2px rgba(103, 85, 251, 0.35);
}

.trust-dossier__amount {
  font-size: clamp(2.25rem, 3.6vw, 3rem);
  line-height: 1;
  letter-spacing: -0.025em;
  color: var(--p-primary-700);
}

.trust-dossier__signal {
  padding: 0.4rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(103, 85, 251, 0.18);
}

.trust-dossier__signal-icon {
  font-size: 12px;
  color: var(--p-primary-700);
}
</style>
